'use client';

import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { registerCompanySchema, type RegisterCompanyInput } from '@zcmss/types';
import { createApiClient, createCentralApi } from '@zcmss/api-client';
import { Button, Input } from '@zcmss/ui';
import { CheckCircle, Loader2, XCircle } from 'lucide-react';

const api = createCentralApi(
  createApiClient({ baseUrl: process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8000/api/v1' })
);

interface RegisterFormProps {
  defaultPlan?: string;
}

function buildPortalUrl(subdomain: string) {
  const baseDomain = process.env.NEXT_PUBLIC_BASE_DOMAIN ?? 'localhost';
  const protocol = typeof window !== 'undefined' ? window.location.protocol : 'http:';
  return `${protocol}//${subdomain}.${baseDomain}:3002/login`;
}

export function RegisterForm({ defaultPlan = 'popular' }: RegisterFormProps) {
  const [subdomainAvailable, setSubdomainAvailable] = useState<boolean | null>(null);
  const [success, setSuccess] = useState<{ portal_url: string; tenant_id: string; subdomain: string } | null>(null);
  const [provisioning, setProvisioning] = useState(false);
  const [provisionError, setProvisionError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<RegisterCompanyInput>({
    resolver: zodResolver(registerCompanySchema),
    defaultValues: {
      plan_slug: defaultPlan as RegisterCompanyInput['plan_slug'],
    },
  });

  const subdomain = watch('subdomain');

  async function checkSubdomain() {
    if (!subdomain || subdomain.length < 3) return;
    try {
      const result = await api.checkSubdomain(subdomain);
      setSubdomainAvailable(result.available);
    } catch {
      setSubdomainAvailable(null);
    }
  }

  async function onSubmit(data: RegisterCompanyInput) {
    setError(null);
    setProvisionError(null);
    try {
      const result = await api.register(data);
      setSuccess({
        portal_url: result.portal_url,
        tenant_id: result.tenant_id,
        subdomain: result.subdomain,
      });

      if (result.status !== 'active') {
        setProvisioning(true);
      }
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Registration failed. Please try again.';
      setError(message);
    }
  }

  useEffect(() => {
    if (!success || !provisioning) return;

    let cancelled = false;

    async function pollStatus() {
      try {
        const tenant = await api.getTenantStatus(success!.tenant_id);

        if (cancelled) return;

        if (tenant.status === 'active') {
          setProvisioning(false);
          return;
        }

        if (tenant.status === 'failed') {
          setProvisioning(false);
          setProvisionError(
            tenant.provisioning_error || 'Provisioning failed. Contact support or retry from the admin portal.'
          );
          return;
        }

        window.setTimeout(pollStatus, 2000);
      } catch {
        if (!cancelled) {
          window.setTimeout(pollStatus, 3000);
        }
      }
    }

    pollStatus();

    return () => {
      cancelled = true;
    };
  }, [success, provisioning]);

  if (success) {
    const portalUrl = buildPortalUrl(success.subdomain);

    return (
      <div className="mt-8 rounded-2xl border border-emerald-200 bg-emerald-50 p-8 text-center dark:border-emerald-800 dark:bg-emerald-950/30">
        {provisioning ? (
          <>
            <Loader2 className="mx-auto h-12 w-12 animate-spin text-indigo-600" />
            <h2 className="mt-4 text-xl font-semibold text-zinc-900 dark:text-zinc-100">Setting up your CRM…</h2>
            <p className="mt-2 text-sm text-zinc-600 dark:text-zinc-400">
              Creating your isolated database and admin account. This usually takes a few seconds.
            </p>
          </>
        ) : (
          <>
            <CheckCircle className="mx-auto h-12 w-12 text-emerald-600" />
            <h2 className="mt-4 text-xl font-semibold text-zinc-900 dark:text-zinc-100">Company created!</h2>
            <p className="mt-2 text-sm text-zinc-600 dark:text-zinc-400">
              Your CRM is ready. Sign in with the admin email and password you just created.
            </p>
            <a href={portalUrl} className="mt-6 inline-block">
              <Button>Go to your portal</Button>
            </a>
          </>
        )}

        {provisionError && (
          <p className="mt-4 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700 dark:bg-red-950/30 dark:text-red-400">
            {provisionError}
          </p>
        )}
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="mt-8 space-y-4">
      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">{error}</div>
      )}

      <Input label="Company Name" error={errors.company_name?.message} {...register('company_name')} />

      <div>
        <Input
          label="Subdomain"
          placeholder="acme"
          error={errors.subdomain?.message}
          {...register('subdomain', { onBlur: checkSubdomain })}
        />
        <div className="mt-1 flex items-center gap-2 text-sm text-zinc-500">
          <span>{subdomain || 'yourcompany'}.zcmss.com</span>
          {subdomainAvailable === true && <CheckCircle className="h-4 w-4 text-emerald-500" />}
          {subdomainAvailable === false && <XCircle className="h-4 w-4 text-red-500" />}
        </div>
      </div>

      <Input label="Admin Name" error={errors.admin_name?.message} {...register('admin_name')} />
      <Input label="Admin Email" type="email" error={errors.admin_email?.message} {...register('admin_email')} />
      <Input
        label="Password"
        type="password"
        error={errors.admin_password?.message}
        {...register('admin_password')}
      />
      <Input
        label="Confirm Password"
        type="password"
        error={errors.admin_password_confirmation?.message}
        {...register('admin_password_confirmation')}
      />

      <div>
        <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300">Plan</label>
        <select
          className="mt-1.5 flex h-10 w-full rounded-xl border border-zinc-200 bg-white px-3 text-sm dark:border-zinc-700 dark:bg-zinc-900"
          {...register('plan_slug')}
        >
          <option value="starter">Starter — $29/mo</option>
          <option value="popular">Popular — $99/mo</option>
          <option value="advance">Advance — $299/mo</option>
        </select>
      </div>

      <Button type="submit" className="w-full" size="lg" loading={isSubmitting}>
        Create Company
      </Button>
    </form>
  );
}
